import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useAtomValue } from 'jotai'
import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import {
  type CoveragesGroupInput,
  CoveragesGroupingCriteria,
  type DateRange,
  DynamicStats,
  HistoricDynamicStatsType,
  type SapioRatingHistoryInput,
  daySinceEpoch,
} from 'types'

import { queryHistoricDynamicStats } from '@api/dynamic-stats/dynamic-stats.queries'

import DateRangeFilterDialog from '@/components/filters/date-range-filter-dialog'
import {
  useDynamicStats,
  useDynamicStatsOldestComputedAt,
} from '@/stores/dynamic-stats.atom'
import { portfolioFilterAtom } from '@/stores/portfolio-filter.atom'

import {
  type AnalysisFilters,
  analysisFiltersSchema,
} from '../-components/analysis-filters.schema'
import AnalysisRightBar from '../-components/analysis-right-bar'
import SectionHeader from '../../-components/section-layout/section-header'
import AmountsEvolutionGraph from './-components/amounts-evolution-graph'
import CoverageRateEvolutionGraph from './-components/coverage-rate-evolution-graph'
import ExposureBySapioRatingGraph from './-components/exposure-by-sapio-rating-graph'
import ExposuresCards from './-components/exposures-cards'
import GeographicalExposure from './-components/geographical-exposure/geographical-exposure'
import RestrictiveDecisionsGraph from './-components/restrictive-decisions-graph'
import SapioRatingHistoryGraph from './-components/sapio-rating-history-graph'

const RATING_DEFAULT_POINTS_COUNT = 30
const HISTORIC_DEFAULT_INTERVAL_DAYS = 30

const DEFAULT_DATE_RANGE: DateRange = {
  from: daySinceEpoch(new Date()) - 365,
  to: daySinceEpoch(new Date()),
}

export const Route = createFileRoute('/app/analysis/exposures/')({
  component: RouteComponent,
  validateSearch: analysisFiltersSchema,
})

function RouteComponent() {
  const { t } = useTranslation()
  const navigate = Route.useNavigate()
  const divToExportRef = useRef(null)
  const search = Route.useSearch()
  const portfolioUserUuid = useAtomValue(portfolioFilterAtom)

  const historicDateRange = search.dateRange ?? DEFAULT_DATE_RANGE

  const filters: CoveragesGroupInput['filters'] = {
    debtorCompanyCountryCodes: search.debtorCompanyCountryCodes,
    groupUuids: search.groupUuids,
    insuranceCompanies: search.insuranceCompanies,
    insuredCompanyCountryCodes: search.insuredCompanyCountryCodes,
    policyUuids: search.policyUuids,
    portfolioUserUuid,
    insuredCompanyUuids: search.insuredCompanyUuids,
  }

  const displayFiltersWarning = !!Object.values(filters).filter((v) => !!v)
    .length

  const sapioRatingHistoryInput: SapioRatingHistoryInput = {
    filters,
    intervals: {
      dateRange: search.dateRange,
      intervalDays: search.dateRange
        ? (search.dateRange.to - search.dateRange.from) /
          RATING_DEFAULT_POINTS_COUNT
        : undefined,
    },
  }

  const coveragesGroupInput: CoveragesGroupInput = {
    filters,
    items: [
      { criteria: CoveragesGroupingCriteria.ByDebtorSapioRating },
      { criteria: CoveragesGroupingCriteria.ByDebtorCountry },
      { criteria: CoveragesGroupingCriteria.ByInsuredCompanyCountry },
      { criteria: 'none' },
    ],
  }

  const sapioRatingHistoryStats = useDynamicStats(
    DynamicStats.SapioRatingHistory,
    sapioRatingHistoryInput,
  )

  const sapioRatingHistory =
    sapioRatingHistoryStats?.state === 'resolved'
      ? sapioRatingHistoryStats.data
      : undefined

  const coveragesGroupStats = useDynamicStats(
    DynamicStats.CoveragesGroup,
    coveragesGroupInput,
  )

  const coveragesGroupData =
    coveragesGroupStats?.state === 'resolved'
      ? coveragesGroupStats.data
      : undefined

  const lastUpdate = useDynamicStatsOldestComputedAt([
    sapioRatingHistoryStats,
    coveragesGroupStats,
  ])

  // Historic dynamic stats - only date range applies, no other filters
  const historicRequest = {
    dateRange: historicDateRange,
    intervalDays: HISTORIC_DEFAULT_INTERVAL_DAYS,
  }

  const { data: historicCoveragesStats } = useQuery({
    ...queryHistoricDynamicStats({
      ...historicRequest,
      type: HistoricDynamicStatsType.CoveragesStats,
    }),
    enabled: !!historicDateRange,
  })

  const { data: historicFlowCoveragesStats } = useQuery({
    ...queryHistoricDynamicStats({
      ...historicRequest,
      type: HistoricDynamicStatsType.FlowCoveragesStats,
    }),
    enabled: !!historicDateRange,
  })

  const { data: historicStatsByDebtorCountry } = useQuery({
    ...queryHistoricDynamicStats({
      ...historicRequest,
      type: HistoricDynamicStatsType.CoveragesStatsByDebtorCountry,
    }),
    enabled: !!historicDateRange,
  })

  const { data: historicStatsByTradeSector } = useQuery({
    ...queryHistoricDynamicStats({
      ...historicRequest,
      type: HistoricDynamicStatsType.CoveragesStatsByTradeSector,
    }),
    enabled: !!historicDateRange,
  })

  const setFilters = (filters: AnalysisFilters) =>
    navigate({ search: (prev) => ({ ...prev, ...filters }) })

  const setDateRangeFilter = (dateRange: DateRange | undefined) =>
    navigate({ search: (prev) => ({ ...prev, dateRange }) })

  return (
    <div className="flex grow flex-col divide-y">
      <SectionHeader
        isSecondary
        title={t('analysis.exposures.title')}
        subtitle={lastUpdate}
      >
        <AnalysisRightBar
          divToExportRef={divToExportRef}
          filters={search}
          setFilters={setFilters}
          filename="coverages-analysis"
        >
          <DateRangeFilterDialog
            value={search.dateRange}
            onChange={setDateRangeFilter}
            className="max-w-56"
          />
        </AnalysisRightBar>
      </SectionHeader>
      <div
        className="flex min-w-0 flex-col gap-2 overflow-x-hidden overflow-y-auto p-2"
        ref={divToExportRef}
      >
        <ExposuresCards
          stats={coveragesGroupData?.['none'].data}
          statsByDebtor={
            coveragesGroupData?.[CoveragesGroupingCriteria.ByDebtorCountry]
          }
        />
        <CoverageRateEvolutionGraph
          stockData={historicCoveragesStats}
          flowData={historicFlowCoveragesStats}
          displayFiltersWarning={displayFiltersWarning}
        />
        <AmountsEvolutionGraph
          countryData={historicStatsByDebtorCountry}
          sectorData={historicStatsByTradeSector}
          displayFiltersWarning={displayFiltersWarning}
        />
        <div className="flex h-fit shrink-0 flex-col gap-2 2xl:flex-row">
          <GeographicalExposure
            view="debtor"
            data={
              coveragesGroupData?.[CoveragesGroupingCriteria.ByDebtorCountry]
            }
          />
          <GeographicalExposure
            view="insured"
            data={
              coveragesGroupData?.[
                CoveragesGroupingCriteria.ByInsuredCompanyCountry
              ]
            }
          />
        </div>
        <RestrictiveDecisionsGraph
          data={historicCoveragesStats}
          displayFiltersWarning={displayFiltersWarning}
        />
        <div className="flex w-full min-w-0 flex-col gap-2 md:flex-row md:items-stretch">
          <ExposureBySapioRatingGraph
            data={
              coveragesGroupData?.[
                CoveragesGroupingCriteria.ByDebtorSapioRating
              ]
            }
          />
          <SapioRatingHistoryGraph data={sapioRatingHistory} />
        </div>
      </div>
    </div>
  )
}
